import { BroadcastCsvHeader } from "components/BroadcastMessage/BroadcastCsvHeader";
import Async from "components/DesignSystem/AsyncComponents/Async";
import Card from "components/DesignSystem/Card/Card";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import DashboardContainer from "components/dashboard/DashboardContainer";
import { DnDArea } from "components/design/UploadFileSection";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { useToast } from "hooks/useToast";
import { useValidateCSVMutation } from "store/apis/broadcastCall";
import { BULK_CALL_CAMPAIGN_EXCEL_TEMPLATE_URL } from "types/contants/sampleExcelSheet";
import { BackendError } from "types/utils/error";
import { BroadcastCallChannelList } from "./BroadcastCallChannelList";

type BroadcastCallProps = {
  file: File | null;
  setChannelLength: (length: number) => void;
  setCSVFile: (file: File | null) => void;
};

export const BroadcastCall = ({
  file,
  setChannelLength,
  setCSVFile,
}: BroadcastCallProps) => {
  const [validateCSV, { data, isLoading, isSuccess, reset }] =
    useValidateCSVMutation();
  const query = useQuery();
  const pageNum = Number(query.get("page_num") || "1");
  const { update: updateQuery } = useUpdateQuery();
  const { alertToast } = useToast();

  const handleFileUpload = async (files: File[]) => {
    try {
      const file = files[0];
      setCSVFile(file);
      const persons = await validateCSV({
        csv_file: file,
        page: pageNum.toString(),
      }).unwrap();
      setChannelLength(persons.pagination?.total_count);
    } catch (error) {
      setCSVFile(null);
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const handlePageChange = (page: number) => {
    updateQuery({
      query: "page_num",
      value: page.toString(),
    });
    if (page !== pageNum && file) {
      handleFileUpload([file]);
    }
  };

  return (
    <div className="t-flex-1 t-space-y-4">
      <BroadcastCsvHeader
        exampleTemplateUrl={BULK_CALL_CAMPAIGN_EXCEL_TEMPLATE_URL}
        customPagination={
          data && (
            <PaginationHandler
              page_num={pageNum}
              per_page={data.pagination?.page_size || 20}
              total_count={data.pagination?.total_count || 0}
              total_pages={data.pagination?.total_pages || 1}
              onPageNumChange={handlePageChange}
            />
          )
        }
        title="Upload CSV File for IVR broadcast"
        onDrop={handleFileUpload}
      />
      <DashboardContainer.Content>
        <div className="t-h-full">
          {isSuccess || isLoading ? (
            <Async.Root
              {...{
                isLoading,
                isSuccess,
                isEmpty: data?.pagination?.total_count === 0,
              }}
              renderOnLoad={
                <Card.Body className="t-space-y-3">
                  <Skeleton className="t-min-h-10" />
                  <Skeleton className="t-min-h-10" />
                  <Skeleton className="t-min-h-10" />
                </Card.Body>
              }
            >
              <Async.Empty>
                <div className="t-flex t-justify-center t-items-center t-h-full t-flex-col">
                  <p className="t-text-subtext t-text-text-100 t-m-0">
                    No channels found
                  </p>
                  <p className="t-text-body-sm t-text-text-30">
                    Please upload another file to see more channels.
                  </p>
                </div>
              </Async.Empty>
              <Async.Success>
                {data && (
                  <BroadcastCallChannelList userDetails={data.call_details} />
                )}
              </Async.Success>
            </Async.Root>
          ) : (
            <DnDArea
              multiple={false}
              onDrop={handleFileUpload}
              accept={{ "text/csv": [".csv"] }}
              title="Click or drag and drop to upload the filled template"
              subText="File Format: csv"
            />
          )}
        </div>
      </DashboardContainer.Content>
    </div>
  );
};
